import logger from '../utils/logger';

const DB_NAME = 'NiceAgendaDB';
const DB_VER = 2;
const STORES = ['appointments', 'patients', 'procedures'] as const;
const SYNC_STORE = 'sync_queue';

export interface SyncQueueItem {
  qid?: number;
  method: string;
  path: string;
  body?: any;
  ts: number;
}

let _db: IDBDatabase | null = null;

function open(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      for (const name of STORES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      }
      if (!db.objectStoreNames.contains(SYNC_STORE)) {
        db.createObjectStore(SYNC_STORE, { keyPath: 'qid', autoIncrement: true });
      }
    };
    req.onsuccess = (e) => {
      _db = (e.target as IDBOpenDBRequest).result;
      _db.onclose = () => { _db = null; };
      resolve(_db);
    };
    req.onerror = (e) => reject((e.target as IDBOpenDBRequest).error);
  });
}

function store(name: string, mode: IDBTransactionMode = 'readonly') {
  return _db!.transaction(name, mode).objectStore(name);
}

export async function getAll<T = any>(storeName: string): Promise<T[]> {
  await open();
  return new Promise((resolve, reject) => {
    const r = store(storeName).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

export async function putAll(storeName: string, records: any[]): Promise<void> {
  await open();
  return new Promise((resolve, reject) => {
    const tx = _db!.transaction(storeName, 'readwrite');
    const s = tx.objectStore(storeName);
    for (const r of records) s.put(r);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function put(storeName: string, record: any): Promise<void> {
  await open();
  return new Promise((resolve, reject) => {
    const r = store(storeName, 'readwrite').put(record);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

export async function del(storeName: string, id: any): Promise<void> {
  await open();
  return new Promise((resolve, reject) => {
    const r = store(storeName, 'readwrite').delete(id);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

export async function addToSyncQueue(item: Omit<SyncQueueItem, 'qid' | 'ts'>): Promise<number> {
  await open();
  return new Promise((resolve, reject) => {
    const r = store(SYNC_STORE, 'readwrite').add({ ...item, ts: Date.now() });
    r.onsuccess = () => resolve(r.result as number);
    r.onerror = () => reject(r.error);
  });
}

export async function getAllSyncQueue(): Promise<SyncQueueItem[]> {
  return getAll<SyncQueueItem>(SYNC_STORE);
}

export async function removeSyncItem(qid: number): Promise<void> {
  return del(SYNC_STORE, qid);
}

export async function getSyncQueueCount(): Promise<number> {
  await open();
  return new Promise((resolve, reject) => {
    const r = store(SYNC_STORE).count();
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export async function initDb(): Promise<void> {
  try {
    await open();
    logger.log('IndexedDB initialized');
  } catch (e) {
    logger.error('IndexedDB init failed:', e);
  }
}

// Wipe all user-specific stores on logout so one user's cached data
// can never leak to the next user who opens the app on the same device.
export async function clearAllStores(): Promise<void> {
  await open();
  return new Promise((resolve, reject) => {
    const storeNames = [...STORES, SYNC_STORE];
    const tx = _db!.transaction(storeNames, 'readwrite');
    for (const name of storeNames) tx.objectStore(name).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

const offlineDb = {
  open: initDb,
  getAll,
  putAll,
  put,
  del,
  addToSyncQueue,
  getAllSyncQueue,
  removeSyncItem,
  getSyncQueueCount,
  clearAllStores,
};

export default offlineDb;
